import { Box } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { MdDownload } from 'react-icons/md';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import { useTutorialActive } from '@/hooks/tutorialsHooks/useTutorialActive';
import { useTourContext } from '@/store/tourContext';
// Import the mock PDF file
import tutorialIVR from '@/utils/tutorials/mockData/tutorial-IVR.pdf';

/**
 * Demo version of IVRViewModal for tutorial purposes.
 * Uses mocked data and doesn't make API calls.
 */
const DemoIVRViewModal = () => {
	const { registerElement } = useTourContext();
	const { isBVTutorialActive: isDemoBVTutorialActive } = useTutorialActive();

	const printButtonRef = useRef<HTMLButtonElement | null>(null);
	const downloadButtonContainerRef = useRef<HTMLDivElement | null>(null);
	const [fileUrl, setFileUrl] = useState<string | null>(null);

	// Load the mock PDF file from mockData
	useEffect(() => {
		setFileUrl(tutorialIVR);
	}, []);

	// Register Print and Download button refs for the tour
	useEffect(() => {
		if (!isDemoBVTutorialActive) {
			registerElement('BVIVRPrintIconRef', null);
			registerElement('BVIVRDownloadIconRef', null);
			return;
		}

		// Wait for buttons to render, then register refs
		const timeoutId = setTimeout(() => {
			if (printButtonRef.current) {
				registerElement('BVIVRPrintIconRef', printButtonRef.current);
			}
			// Find Download button within the container
			if (downloadButtonContainerRef.current) {
				const downloadButton = downloadButtonContainerRef.current.querySelector('button');
				if (downloadButton) {
					registerElement('BVIVRDownloadIconRef', downloadButton as HTMLElement);
				}
			}
		}, 100);

		return () => {
			clearTimeout(timeoutId);
			if (isDemoBVTutorialActive) {
				registerElement('BVIVRPrintIconRef', null);
				registerElement('BVIVRDownloadIconRef', null);
			}
		};
	}, [isDemoBVTutorialActive, registerElement]);



	const handleDownload = () => {
		// Mock download action for demo
		if (fileUrl) {
			const filename = 'tutorial-IVR.pdf';
			const link = document.createElement('a');
			link.href = fileUrl;
			link.download = filename;
			link.target = '_blank';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}
	};

	return (
		<Box p={4}>
			<Box>
				<Box
					display="flex"
					justifyContent="flex-end"
					alignItems="center"
					gap={2}
					mb={4}
				>

					<Box ref={downloadButtonContainerRef}>
						<FaceliftButton
							onClick={handleDownload}
							size="sm"
							display="flex"
							alignItems="center"
							gap={2}
						>
							<MdDownload />
							Download
						</FaceliftButton>
					</Box>
				</Box>

				{fileUrl && (
					<Box
						border="1px solid"
						borderColor="gray.200"
						borderRadius="md"
						overflow="hidden"
						height="600px"
					>
						<iframe
							src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=0`}
							width="100%"
							height="100%"
							title="IVR Document (Demo)"
							style={{ border: 'none' }}
							loading="lazy"
						/>
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default DemoIVRViewModal;
